/**
 * Permission codes the property-management back office declares an interest in.
 *
 * These are the dot-strings `internal/pms/permissions.go` registers with the
 * RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. Nothing here is
 * a security boundary — the server re-checks every route and again in the
 * service layer — but an action a user cannot perform should not be rendered
 * disabled, it should not be rendered at all (§11).
 *
 * **Four segregation-of-duties pairs run through C9 and no screen ever infers
 * one half from the other** (C9 §6.1). Only two of them touch the commercial
 * screens in this file, and both shape a control here:
 *
 *  - `pms.rate.manage` / `pms.rate.publish` — editing a rate grid and putting
 *    the plan live are separate authorities, so the plan editor never offers
 *    "save and activate" as one button. Activation submits the plan for
 *    approval (§4.12); it is not a save.
 *  - `pms.reservation.amend` / `pms.reservation.cancel` — moving a booking and
 *    killing it are different acts with different money consequences, because a
 *    cancellation applies the *snapshotted* policy and an amendment re-quotes.
 *
 * The other two (`pms.folio.allowance` ⊗ `pms.night_audit.run`,
 * `pms.housekeeping.status_set` ⊗ `pms.housekeeping.inspect`) belong to the
 * operations screens and are listed there.
 *
 * `moduleManage` guards no route. It is the `<module>.manage` fallback
 * `core/rules` consults when no authority band covers an overridable rule's doc
 * type; without it a rate override returns `ERR_OVERRIDE_NOT_AUTHORISED` on a
 * tenant with no matrix. It is listed so a screen can explain a refusal rather
 * than merely report one.
 *
 * The codes at the foot belong to other modules and are here because these
 * screens cannot do their job without them: the company a property belongs to,
 * the GL accounts the settings map onto, and the item a room type sells as.
 */

export const PMS_PERMISSIONS = {
  /* masters — §6.1 family 1 */
  propertyRead: 'pms.property.read',
  propertyManage: 'pms.property.manage',
  roomManage: 'pms.room.manage',
  roomOutOfOrder: 'pms.room.out_of_order',
  settingsManage: 'pms.settings.manage',
  /** Not a route guard — the authority fallback for an overridable rule. */
  moduleManage: 'pms.module.manage',

  /* guests — §6.1 family 2 */
  guestRead: 'pms.guest.read',
  guestManage: 'pms.guest.manage',
  /** An audited reveal, MFA-stepped. Never inferred from `guestManage`. */
  guestRevealDocument: 'pms.guest.reveal_document',
  guestRestrict: 'pms.guest.restrict',
  guestMessage: 'pms.guest.message',

  /* rates — §6.1 family 3 */
  rateRead: 'pms.rate.read',
  rateManage: 'pms.rate.manage',
  /** SoD partner of `rateManage`: putting a plan live is its own authority. */
  ratePublish: 'pms.rate.publish',
  rateOverride: 'pms.rate.override',
  restrictionManage: 'pms.restriction.manage',

  /* availability — §6.1 family 4 */
  availabilityRead: 'pms.availability.read',
  availabilityOverride: 'pms.availability.override',

  /* reservations — §6.1 family 5 */
  reservationRead: 'pms.reservation.read',
  reservationCreate: 'pms.reservation.create',
  reservationAmend: 'pms.reservation.amend',
  reservationCancel: 'pms.reservation.cancel',
  blockManage: 'pms.block.manage',
  blockConfirm: 'pms.block.confirm',
} as const

/** Codes owned by other modules that the commercial screens read through. */
export const PMS_EXTERNAL_PERMISSIONS = {
  companyRead: 'org.company.read',
  locationRead: 'org.location.read',
  accountRead: 'org.account.read',
  itemRead: 'inventory.item.read',
} as const

export type PmsPermission = (typeof PMS_PERMISSIONS)[keyof typeof PMS_PERMISSIONS]
